import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ConsentType,
  hasValidConsent,
  getAllConsents,
  recordConsent,
  revokeConsent,
  hasAllRequiredConsents,
  getMissingRequiredConsents,
  CONSENT_LABELS,
  REQUIRED_CONSENTS
} from '../services/consentService';

// ============================================
// USE CONSENT HOOK - React integration for GDPR consents
// ============================================

export interface UseConsentReturn {
  /** All consent statuses for current user */
  consents: Record<ConsentType, boolean> | null;
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Give consent for a type */
  giveConsent: (type: ConsentType) => Promise<boolean>;
  /** Revoke consent for a type */
  removeConsent: (type: ConsentType, reason?: string) => Promise<boolean>;
  /** Check if a specific consent is valid */
  checkConsent: (type: ConsentType) => Promise<boolean>;
  /** Refresh consents from server */
  refreshConsents: () => Promise<void>;
  /** Check if all required consents are given */
  hasAllRequired: boolean;
  /** List of missing required consents */
  missingRequired: ConsentType[];
}

/**
 * Hook to manage user consents
 * Provides real-time consent status and management functions
 */
export function useConsent(): UseConsentReturn {
  const { user } = useAuth();
  const [consents, setConsents] = useState<Record<ConsentType, boolean> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAllRequired, setHasAllRequired] = useState(false);
  const [missingRequired, setMissingRequired] = useState<ConsentType[]>([]);

  // Load consents when user changes
  const loadConsents = useCallback(async () => {
    if (!user) {
      setConsents(null);
      setLoading(false);
      setHasAllRequired(false);
      setMissingRequired(REQUIRED_CONSENTS);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await getAllConsents();
      setConsents(result);

      // Calculate required status
      const allRequired = await hasAllRequiredConsents();
      setHasAllRequired(allRequired);

      const missing = await getMissingRequiredConsents();
      setMissingRequired(missing);

    } catch (err) {
      console.error('Failed to load consents:', err);
      setError('Error al cargar consentimientos');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadConsents();
  }, [loadConsents]);

  // Give consent
  const giveConsent = useCallback(async (type: ConsentType): Promise<boolean> => {
    try {
      setError(null);
      const success = await recordConsent(type, true);

      if (success) {
        // Update local state immediately
        setConsents(prev => prev ? { ...prev, [type]: true } : null);

        // Recalculate required status
        const allRequired = await hasAllRequiredConsents();
        setHasAllRequired(allRequired);

        const missing = await getMissingRequiredConsents();
        setMissingRequired(missing);
      } else {
        setError('Error al registrar consentimiento');
      }

      return success;
    } catch (err) {
      console.error('Failed to give consent:', err);
      setError('Error al registrar consentimiento');
      return false;
    }
  }, []);

  // Remove/revoke consent
  const removeConsent = useCallback(async (type: ConsentType, reason?: string): Promise<boolean> => {
    try {
      setError(null);
      const success = await revokeConsent(type, reason);

      if (success) {
        // Update local state immediately
        setConsents(prev => prev ? { ...prev, [type]: false } : null);

        // Recalculate required status
        const allRequired = await hasAllRequiredConsents();
        setHasAllRequired(allRequired);

        const missing = await getMissingRequiredConsents();
        setMissingRequired(missing);
      } else {
        setError('Error al revocar consentimiento');
      }

      return success;
    } catch (err) {
      console.error('Failed to remove consent:', err);
      setError('Error al revocar consentimiento');
      return false;
    }
  }, []);

  // Check single consent
  const checkConsent = useCallback(async (type: ConsentType): Promise<boolean> => {
    return await hasValidConsent(type);
  }, []);

  return {
    consents,
    loading,
    error,
    giveConsent,
    removeConsent,
    checkConsent,
    refreshConsents: loadConsents,
    hasAllRequired,
    missingRequired
  };
}

// ============================================
// Additional Utility Hooks
// ============================================

/**
 * Hook to check a single consent type
 * Useful for conditional rendering based on consent
 */
export function useConsentCheck(type: ConsentType): {
  hasConsent: boolean;
  loading: boolean;
} {
  const [hasConsent, setHasConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;

    async function check() {
      if (!user) {
        setHasConsent(false);
        setLoading(false);
        return;
      }

      try {
        const result = await hasValidConsent(type);
        if (mounted) {
          setHasConsent(result);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to check consent:', err);
        if (mounted) {
          setHasConsent(false);
          setLoading(false);
        }
      }
    }

    check();

    return () => {
      mounted = false;
    };
  }, [user, type]);

  return { hasConsent, loading };
}

/**
 * Hook for signup page - manages multiple consent checkboxes
 * Note: ai_analysis and third_party are implicit in accepting Terms/Privacy
 * (covered in those documents) but recorded separately for audit trail
 */
export interface SignupConsents {
  terms: boolean;
  privacy: boolean;
  ai_analysis: boolean;
  third_party: boolean;
  marketing: boolean;
}

export function useSignupConsents() {
  const [consents, setConsents] = useState<SignupConsents>({
    terms: false,
    privacy: false,
    ai_analysis: false,
    third_party: false,
    marketing: false
  });

  const toggleConsent = useCallback((type: keyof SignupConsents) => {
    setConsents(prev => ({ ...prev, [type]: !prev[type] }));
  }, []);

  // When setting terms or privacy, also set ai_analysis and third_party
  // (they are covered in Terms/Privacy documents, but recorded separately for audit)
  const setConsentValue = useCallback((type: keyof SignupConsents, value: boolean) => {
    setConsents(prev => {
      const updated = { ...prev, [type]: value };
      // If both terms and privacy are accepted, implicitly accept ai_analysis and third_party
      if (updated.terms && updated.privacy) {
        updated.ai_analysis = true;
        updated.third_party = true;
      } else {
        // If either is unchecked, also uncheck the implicit ones
        updated.ai_analysis = false;
        updated.third_party = false;
      }
      return updated;
    });
  }, []);

  // Only terms and privacy are required in UI (ai_analysis/third_party are implicit)
  const areRequiredComplete = consents.terms && consents.privacy;

  const getMissingLabels = useCallback((): string[] => {
    const missing: string[] = [];
    const required: (keyof SignupConsents)[] = ['terms', 'privacy'];

    for (const type of required) {
      if (!consents[type]) {
        missing.push(CONSENT_LABELS[type]);
      }
    }

    return missing;
  }, [consents]);

  return {
    consents,
    setConsents,
    toggleConsent,
    setConsentValue,
    areRequiredComplete,
    getMissingLabels
  };
}

// Re-export types and constants for convenience
export type { ConsentType };
export { CONSENT_LABELS, REQUIRED_CONSENTS };
